import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { View, Spinner, Text } from '@smarterservices/smarter-ui';
import { SessionViewer } from '@ui/shared/components/proctoring/SessionViewer/';
import config from '../../config';

/**
 * SingleSessionViewer Component
 *
 * Renders a standalone session viewer for a specific session.
 * Opens in a new window/tab and loads the session viewer with full functionality.
 *
 * @component
 * @returns {JSX.Element} The single session viewer component
 */
function SingleSessionViewer() {
    const { installSid, examSid, sessionSid } = useParams();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [spToken, setSpToken] = useState(null);
    const [sessionData, setSessionData] = useState(null);
    const sessionViewerRef = useRef(null);

    useEffect(() => {
        // Get token from URL query parameter
        const tokenFromUrl = searchParams.get('token');

        // Validate required parameters
        if (!installSid || !examSid || !sessionSid) {
            setError('Missing required parameters');
            setIsLoading(false);
            return;
        }

        if (!tokenFromUrl) {
            setError('Authentication token required');
            setIsLoading(false);
            return;
        }

        setSpToken(tokenFromUrl);
        setIsLoading(false);
    }, [installSid, examSid, sessionSid, searchParams]);

    if (isLoading) {
        return (
            <View
                as="div"
                height="100vh"
                display="flex"
                justifyItems="center"
                alignItems="center"
            >
                <Spinner renderTitle="Loading session..." size="large" />
            </View>
        );
    }

    if (error) {
        return (
            <View
                as="div"
                height="100vh"
                display="flex"
                justifyItems="center"
                alignItems="center"
                padding="large"
            >
                <Text size="large" color="danger">
                    Error: {error}
                </Text>
            </View>
        );
    }

    return (
        <View
            as="div"
            height="100vh"
            width="100%"
            overflow="hidden"
        >
            <SessionViewer
                ref={sessionViewerRef}
                sessionSid={sessionSid}
                examSid={examSid}
                installSid={installSid}
                apiUrl={config.proctoring?.api?.baseUrl}
                roomId={sessionSid}
                spToken={spToken}
                identity={`proctor_${sessionSid}`}
                websocketUrl={config.media?.livekit?.websocketUrl}
                type="proctor"
                video={{
                    sources: ['recorded'],
                    default: 'recorded'
                }}
                enabledComponents={{
                    sessionHeader: true,
                    sessionDetails: {
                        defaultTab: 'flags',
                        defaultCollapsed: false,
                        tabs: ['info', 'identity', 'flags', 'incidentReport', 'communication'],
                    },
                    controls: true,
                    timeline: true,
                    chat: true,
                    microphone: false,
                    camera: false,
                    settings: true,
                }}
                onSessionLoaded={({ userData, sessionData }) => {
                    document.title = `${userData?.firstName} ${userData?.lastName} - ${sessionData?.exam?.title}`;
                }}
            />
        </View>
    );
}

export default SingleSessionViewer;
